/**
 * Sincronización de Regalos en el Carrito (WIM)
 * Añade o elimina automáticamente productos de regalo en función de los disparadores del carrito.
 */
(function () {
  let isSyncing = false;
  let syncTimeout = null;
  let needsSyncAgain = false;

  // Registro en memoria de regalos que no se pudieron añadir (ej. error 422 por falta de stock o variante inexistente)
  const failedGiftIds = new Set();
  const resolvedVariantCache = {};
  const variantToHandleMap = {};

  // Control de circuito para evitar bucles de sincronización rápida
  let syncAttempts = 0;
  let lastSyncWindow = Date.now();

  function checkCircuitBreaker() {
    const now = Date.now();
    if (now - lastSyncWindow > 5000) {
      lastSyncWindow = now;
      syncAttempts = 0;
      return true;
    }
    syncAttempts++;
    if (syncAttempts > 5) {
      console.warn('[wim-cart-gift-sync] Demasiadas sincronizaciones consecutivas. Deteniendo temporalmente para proteger el carrito.');
      return false;
    }
    return true;
  }

  function isGiftFailed(variantId) {
    if (!variantId) return true;
    const strId = variantId.toString();
    if (failedGiftIds.has(strId)) return true;
    try {
      return sessionStorage.getItem('wim_failed_gift_' + strId) === 'true';
    } catch (e) {
      return false;
    }
  }

  function markGiftFailed(variantId) {
    if (!variantId) return;
    const strId = variantId.toString();
    failedGiftIds.add(strId);
    try {
      sessionStorage.setItem('wim_failed_gift_' + strId, 'true');
    } catch (e) { }
  }

  function decodeHTML(html) {
    if (!html) return '';
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  /**
   * Resuelve el ID de variante a partir del handle de un producto
   */
  async function resolveVariantFromHandle(handle) {
    if (!handle) return null;
    if (resolvedVariantCache[handle]) return resolvedVariantCache[handle];

    try {
      const prodRes = await originalFetch(`/products/${handle}.js`, {
        headers: { 'X-Requested-With': 'wim-gift-sync' }
      });
      if (prodRes.ok) {
        const prodData = await prodRes.json();
        const availableVariant = prodData.variants.find(v => v.available) || prodData.variants[0];
        if (availableVariant) {
          const vId = availableVariant.id.toString();
          resolvedVariantCache[handle] = vId;
          return vId;
        }
      }
    } catch (e) {
      console.warn('[wim-cart-gift-sync] Error al resolver variante desde handle ' + handle, e);
    }
    return null;
  }

  /**
   * Resuelve el ID de variante a partir de un ID de producto utilizando la API de Storefront.
   * Esto soluciona el error 422 ("Cannot find variant") cuando la promoción configuró un Product ID en vez de Variant ID.
   */
  async function resolveVariantFromProductId(productId) {
    if (!productId) return null;
    const strId = productId.toString();
    if (resolvedVariantCache[strId]) return resolvedVariantCache[strId];

    try {
      const res = await originalFetch(`/search/suggest.json?q=id:${strId}&resources[type]=product`, {
        headers: { 'X-Requested-With': 'wim-gift-sync' }
      });
      if (!res.ok) return null;
      const data = await res.json();
      const product = data?.resources?.results?.products?.[0];
      if (product?.handle) {
          const vId = await resolveVariantFromHandle(product.handle);
          if (vId) {
            resolvedVariantCache[strId] = vId;
            return vId;
          }
        }
      } catch (e) {
        console.warn('[wim-cart-gift-sync] Error al resolver variante para producto ' + strId, e);
      }
      return null;
    }

  /**
   * Determina si el ID proporcionado es un Product GID y lo resuelve a Variant ID
   */
  async function resolveToVariantId(gidOrId) {
      if (!gidOrId) return null;
      const str = gidOrId.toString();
      const id = str.split('/').pop();
      if (resolvedVariantCache[id]) return resolvedVariantCache[id];

      const isProductGid = str.includes('/Product/') && !str.includes('/ProductVariant/');
      if (isProductGid) {
        const resolved = await resolveVariantFromProductId(id);
        if (resolved) {
          resolvedVariantCache[id] = resolved;
          return resolved;
        }
      }

      return id;
    }

    /**
     * Normaliza los diferentes formatos de payload de regalos generados por el backend/metafield:
     * - Array clásico: [{ variantId: "..." }]
     * - Objeto moderno: { rewardVariantIds: ["..."], rewardProductHandles: ["..."] }
     * - Objeto mapeado por variante: { "56367438...": [{ variantId: "..." }] }
     * - Arrays simples de IDs numéricos o cadenas
     */
    function normalizeRewards(parsedRewards, currentVariantId) {
      if (!parsedRewards) return [];

      // Si es un objeto indexado por variante del disparador
      if (!Array.isArray(parsedRewards) && currentVariantId && parsedRewards[currentVariantId]) {
        return normalizeRewards(parsedRewards[currentVariantId], currentVariantId);
      }

      // Si tiene rewardVariantIds: { rewardVariantIds: [...] }
      if (!Array.isArray(parsedRewards) && Array.isArray(parsedRewards.rewardVariantIds)) {
      return parsedRewards.rewardVariantIds.map((vId, idx) => ({
            variantId: vId,
            productHandle: (parsedRewards.rewardProductHandles && parsedRewards.rewardProductHandles[idx]) ? parsedRewards.rewardProductHandles[idx] : null,
            productId: (parsedRewards.rewardProductIds && parsedRewards.rewardProductIds[idx]) ? parsedRewards.rewardProductIds[idx] : null
          }));
        }

    // Si es un único objeto con variantId o id: { variantId: ... }
    if (!Array.isArray(parsedRewards) && (parsedRewards.variantId || parsedRewards.rewardVariantId || parsedRewards.id)) {
          return [{
        variantId: parsedRewards.variantId || parsedRewards.rewardVariantId || parsedRewards.id,
            productHandle: parsedRewards.productHandle || parsedRewards.rewardProductHandle || null,
            productId: parsedRewards.productId || parsedRewards.rewardProductId || null
          }];
        }

        if (Array.isArray(parsedRewards)) {
          return parsedRewards.map(item => {
            if (typeof item === 'string' || typeof item === 'number') {
              return { variantId: item.toString() };
            }
            return {
          variantId: item.variantId || item.rewardVariantId || item.id || item.variant_id,
              productHandle: item.productHandle || item.rewardProductHandle || null,
              productId: item.productId || item.rewardProductId || null
            };
          }).filter(item => Boolean(item.variantId));
        }

        return [];
      }

      async function syncGifts(prefetchedCart = null) {
        if (isSyncing) {
          needsSyncAgain = true;
          console.log('[wim-cart-gift-sync] Ya se está sincronizando. Encolando siguiente sincronización.');
          return;
        }

        if (!checkCircuitBreaker()) {
          return;
        }

        isSyncing = true;
        needsSyncAgain = false;

        console.log('[wim-cart-gift-sync] Iniciando sincronización...');

        try {
          // 1. Obtiene el estado actual del carrito (precargado o mediante consulta directa)
          let cart = prefetchedCart;
          if (!cart) {
            let res;
            try {
              res = await originalFetch('/cart.js', {
                headers: { 'X-Requested-With': 'wim-gift-sync' }
              });
            } catch (e) {
              isSyncing = false;
              return;
            }
            if (!res.ok) {
              isSyncing = false;
              return;
            }
            cart = await res.json();
          }

          // 2. Construye un mapeo de variante -> regalos basándose en el DOM (donde Liquid inyectó los metafields)
          const variantRewardsMapping = {};
          const domGiftVariantIds = new Set();
          const domItems = document.querySelectorAll('.cart__item');

          domItems.forEach(el => {
            const role = el.getAttribute('data-promo-role');
            const autoAdd = el.getAttribute('data-promo-auto-add');
            const rewardsRaw = el.getAttribute('data-promo-rewards');
            const isGiftAttr = el.getAttribute('data-is-gift') === 'true';
            const itemVariantId = el.getAttribute('data-variant-id');

            if ((isGiftAttr || role === 'reward') && itemVariantId) {
              domGiftVariantIds.add(itemVariantId);
            }

            // Treat as trigger if role is explicitly 'trigger', or if role is missing but there are rewards
            if ((role === 'trigger' || (!role && rewardsRaw && rewardsRaw !== 'null')) && autoAdd !== 'false') {
              if (rewardsRaw) {
                try {
                  const rewardsStr = decodeHTML(rewardsRaw);
                  const variantId = el.getAttribute('data-variant-id');
                  if (variantId) {
                    let parsedRewards = JSON.parse(rewardsStr);
                    const normalized = normalizeRewards(parsedRewards, variantId);
                    if (normalized.length > 0) {
                      variantRewardsMapping[variantId] = normalized;
                    }
                  }
                } catch (e) {
                  console.error('[wim-cart-gift-sync] Error al parsear JSON de regalos', e);
                }
              }
            }
          });

          // Limpia la inactividad y listas negras de sessionStorage para los regalos cuyos disparadores ya no están en el carrito
          const cartTriggerVariants = cart.items.map(item => item.variant_id.toString());
          Object.keys(variantRewardsMapping).forEach(triggerId => {
            if (!cartTriggerVariants.includes(triggerId)) {
              const rewards = variantRewardsMapping[triggerId];
              if (rewards && rewards.length > 0) {
                const rewardVariantGid = rewards[0].variantId;
                const rewardVariantId = rewardVariantGid ? rewardVariantGid.toString().split('/').pop() : null;
                if (rewardVariantId) {
                  try {
                    sessionStorage.removeItem('wim_inactive_gift_' + rewardVariantId);
                    sessionStorage.removeItem('wim_failed_gift_' + rewardVariantId);
                    failedGiftIds.delete(rewardVariantId);
                  } catch (e) { }
                }
              }
            }
          });

          // 3. Calcula los regalos requeridos basándose en los artículos REALES del carrito, NO en el DOM
          const requiredRewards = {}; // variantId -> cantidad necesaria

          for (const item of cart.items) {
            const variantId = item.variant_id.toString();
            const rewards = variantRewardsMapping[variantId];
            if (rewards && rewards.length > 0) {
              const rewardObj = rewards[0];
              const rewardVariantGid = rewardObj.variantId;
              const rewardVariantId = await resolveToVariantId(rewardVariantGid);

              if (rewardVariantId) {
                if (rewardObj.productHandle) {
                  variantToHandleMap[rewardVariantId] = rewardObj.productHandle;
                }

                let isInactive = false;
                try {
                  isInactive = sessionStorage.getItem('wim_inactive_gift_' + rewardVariantId) === 'true';
                } catch (e) { }

                // Solo requiere el regalo si no está marcado como inactivo ni ha fallado previamente
                if (!isInactive && !isGiftFailed(rewardVariantId)) {
                  // Un regalo por cada unidad del producto disparador
                  requiredRewards[rewardVariantId] = (requiredRewards[rewardVariantId] || 0) + item.quantity;
                }
              }
            }
          }

          // 4. Compara los requerimientos con los artículos reales del carrito
          let updates = {};
          let needsUpdate = false;

          // Realiza un seguimiento de cuántos regalos de cada tipo ya están en el carrito
          const currentRewards = {};
          cart.items.forEach(item => {
            const variantId = item.variant_id.toString();
            // Solo considera artículos que forman parte de la lista de regalos requeridos
            if (requiredRewards[variantId] !== undefined) {
              currentRewards[variantId] = (currentRewards[variantId] || 0) + item.quantity;
            }
          });

          // Comprueba si hay regalos en el carrito que ya no deberían estar (sin disparador o con exceso de unidades)
          const allocatedRewards = {};
          cart.items.forEach(item => {
            const variantId = item.variant_id.toString();
            let isGiftProperty = false;
            if (item.properties) {
              if (Array.isArray(item.properties)) {
                isGiftProperty = item.properties.some(p => p[0] === '_is_gift' && p[1] === 'true');
              } else {
                isGiftProperty = item.properties['_is_gift'] === 'true';
              }
            }

            const isZeroPrice = (item.original_price === 0 || item.price === 0 || item.final_price === 0);
            const isGiftItem = isGiftProperty || isZeroPrice || domGiftVariantIds.has(variantId);

            if (isGiftItem) {
              const maxAllowed = requiredRewards[variantId] || 0;
              const isNotFree = !isZeroPrice && item.final_price > 0;

              if (isNotFree) {
                console.warn(`[wim-cart-gift-sync] El regalo ${variantId} no es gratuito (precio: ${item.final_price}). Marcando promoción como inactiva.`);
                try {
                  sessionStorage.setItem('wim_inactive_gift_' + variantId, 'true');
                } catch (e) { }
              }

              if (maxAllowed === 0 || isNotFree) {
                // Este regalo no debería estar aquí (sin disparador o huérfano), se elimina
                console.log(`[wim-cart-gift-sync] Eliminando regalo no autorizado o huérfano: ${variantId} (key: ${item.key})`);
                updates[item.key] = 0;
                needsUpdate = true;
              } else {
                const currentAllocated = allocatedRewards[variantId] || 0;
                const remainingAllowed = maxAllowed - currentAllocated;

                if (remainingAllowed <= 0) {
                  // Cupo superado en líneas previas de la misma variante
                  console.log(`[wim-cart-gift-sync] Eliminando exceso de regalo: ${variantId} (key: ${item.key})`);
                  updates[item.key] = 0;
                  needsUpdate = true;
                } else if (item.quantity > remainingAllowed) {
                  // Ajustar la cantidad al cupo permitido
                  console.log(`[wim-cart-gift-sync] Ajustando cantidad de regalo ${variantId} de ${item.quantity} a ${remainingAllowed}`);
                  updates[item.key] = remainingAllowed;
                  allocatedRewards[variantId] = maxAllowed;
                  needsUpdate = true;
                } else {
                  allocatedRewards[variantId] = currentAllocated + item.quantity;
                }
              }
            }
          });

          const itemsToAdd = [];
          Object.keys(requiredRewards).forEach(variantId => {
            const requiredQty = requiredRewards[variantId];
            const currentQty = currentRewards[variantId] || 0;

            if (currentQty < requiredQty) {
              // Se necesita añadir más unidades del regalo
              const qtyToAdd = requiredQty - currentQty;
              itemsToAdd.push({
                id: variantId,
                quantity: qtyToAdd,
                properties: {
                  '_is_gift': 'true'
                }
              });
              needsUpdate = true;
            }
          });

          // 5. Aplica los cambios mediante la API del carrito de Shopify de forma segura
          if (needsUpdate) {
            let hasChanges = false;

            // Aplica las eliminaciones o ajustes de cantidad
            if (Object.keys(updates).length > 0) {
              try {
                const updateRes = await originalFetch('/cart/update.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'wim-gift-sync'
                  },
                  body: JSON.stringify({ updates })
                });
                if (updateRes.ok) {
                  hasChanges = true;
                } else {
                  console.warn('[wim-cart-gift-sync] Error al actualizar carrito:', updateRes.status);
                }
              } catch (updateErr) {
                console.error('[wim-cart-gift-sync] Excepción al actualizar carrito:', updateErr);
              }
            }

            // Aplica las adiciones de nuevos regalos con verificación estricta de estado HTTP y auto-resolución
            if (itemsToAdd.length > 0) {
              try {
                const addRes = await originalFetch('/cart/add.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'wim-gift-sync'
                  },
                  body: JSON.stringify({ items: itemsToAdd })
                });

                if (addRes.ok) {
                  hasChanges = true;
                } else {
                  let errData = null;
                  try {
                    errData = await addRes.json();
                  } catch (parseE) {
                    errData = addRes.statusText;
                  }
                  console.warn(`[wim-cart-gift-sync] No se pudo añadir el regalo (HTTP ${addRes.status}):`, errData);

                  // Si Shopify indica que no encuentra la variante ("Cannot find variant"), intenta auto-recuperar con la variante activa real
                  const isVariantNotFound = errData && (
                    errData.description === 'Cannot find variant' ||
                    (errData.message && errData.message.includes('variant'))
                  );

                  if (isVariantNotFound) {
                    for (const failedItem of itemsToAdd) {
                        let realVariantId = null;

                        // 1. Si tenemos el handle del producto, obtenemos directamente su variante activa actual
                        const handle = variantToHandleMap[failedItem.id];
                        if (handle) {
                          realVariantId = await resolveVariantFromHandle(handle);
                        }

                        // 2. Si no teníamos handle o falló, intentamos resolver por ID de producto
                        if (!realVariantId) {
                          realVariantId = await resolveVariantFromProductId(failedItem.id);
                        }

                        if (realVariantId && realVariantId !== failedItem.id) {
                          console.log(`[wim-cart-gift-sync] Variante no encontrada (${failedItem.id}). Reintentando con variante activa actual: ${realVariantId}`);
                          try {
                            const retryRes = await originalFetch('/cart/add.js', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'X-Requested-With': 'wim-gift-sync'
                              },
                              body: JSON.stringify({
                                items: [{
                                  id: realVariantId,
                                  quantity: failedItem.quantity,
                                  properties: failedItem.properties
                                }]
                              })
                            });
                            if (retryRes.ok) {
                              hasChanges = true;
                            } else {
                              markGiftFailed(realVariantId);
                              markGiftFailed(failedItem.id);
                            }
                          } catch (retryErr) {
                            markGiftFailed(failedItem.id);
                          }
                        } else {
                          markGiftFailed(failedItem.id);
                        }
                      }
                    } else {
                      // Marcar los regalos fallidos para evitar reintentos infinitos que bloqueen la tienda
                      itemsToAdd.forEach(item => {
                        markGiftFailed(item.id);
                      });
                    }
                  }
                } catch (addErr) {
                  console.error('[wim-cart-gift-sync] Excepción de red al añadir regalos:', addErr);
                }
              }

        // Dispara el evento nativo de FoxTheme ÚNICAMENTE si efectivamente se aplicó algún cambio con éxito
        if (hasChanges) {
                console.log('[wim-cart-gift-sync] Sincronización completa. Despachando theme:cart:refresh');
                document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
              }
            }
          } catch (e) {
            console.error('[wim-cart-gift-sync] Error al sincronizar el carrito', e);
          } finally {
            // Aplica un margen de retraso de seguridad antes de permitir una nueva sincronización
            setTimeout(() => {
              isSyncing = false;
              if (needsSyncAgain) {
                console.log('[wim-cart-gift-sync] Procesando solicitud de sincronización encolada');
                syncGifts();
              }
            }, 800);
          }
        }

  function scheduleSync() {
          clearTimeout(syncTimeout);
          syncTimeout = setTimeout(() => syncGifts(), 150);
        }

        // Intercepta peticiones fetch para capturar llamadas AJAX del carrito de forma fiable
        const originalFetch = window.fetch;
        window.fetch = async function () {
          const arg = arguments[0];
          const options = arguments[1] || {};
          let url = '';
          if (typeof arg === 'string') {
            url = arg;
          } else if (arg && arg.url) {
            url = arg.url;
          }

          // Ignora llamadas internas originadas por el propio script de sincronización
          const isInternal = options.headers && (
            (options.headers['X-Requested-With'] === 'wim-gift-sync') ||
            (options.headers.get && options.headers.get('X-Requested-With') === 'wim-gift-sync')
          );

          const isCartRequest = url && url.includes('/cart/') && !isInternal;
          if (isCartRequest) {
            console.log(`[wim-cart-gift-sync] Fetch interceptado hacia: ${url}`, arguments[1]);
          }

          try {
            const response = await originalFetch.apply(this, arguments);

            if (isCartRequest) {
              console.log(`[wim-cart-gift-sync] Respuesta fetch para ${url}: estado ${response.status}`);

              if (!isSyncing && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))) {
                // Si es un cambio o actualización exitoso, la respuesta ya contiene el JSON del carrito
                // Lo usamos directamente para ahorrarnos una llamada de tipo GET a /cart.js
                if (response.ok && (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))) {
                  try {
                    const clone = response.clone();
                    const cartJson = await clone.json();
                    console.log('[wim-cart-gift-sync] Sincronizando al instante usando JSON precargado');
                    syncGifts(cartJson);
                  } catch (e) {
                    scheduleSync();
                  }
                } else if (response.ok) {
                  scheduleSync();
                }
              }
            }
            return response;
          } catch (err) {
            if (isCartRequest) {
              console.error(`[wim-cart-gift-sync] Error en fetch para ${url}:`, err);
            }
            throw err;
          }
        };

        // Intercepta peticiones XMLHttpRequest como respaldo (fallback)
        const originalXHROpen = window.XMLHttpRequest.prototype.open;
        window.XMLHttpRequest.prototype.open = function (method, url) {
          this._url = url;
          return originalXHROpen.apply(this, arguments);
        };

        const originalXHRSend = window.XMLHttpRequest.prototype.send;
        window.XMLHttpRequest.prototype.send = function () {
          const xhr = this;
          const isCartRequest = xhr._url && typeof xhr._url === 'string' && xhr._url.includes('/cart/');

          if (isCartRequest) {
            console.log(`[wim-cart-gift-sync] XHR interceptado hacia: ${xhr._url}`);
          }

          xhr.addEventListener('load', function () {
            if (isCartRequest) {
              console.log(`[wim-cart-gift-sync] Respuesta XHR para ${xhr._url}: estado ${xhr.status}`);
              if (xhr.status >= 400) {
                console.error(`[wim-cart-gift-sync] Error en petición de carrito XHR. Respuesta:`, xhr.responseText);
              }

              if (!isSyncing && xhr.status < 400 && (xhr._url.includes('/cart/add') || xhr._url.includes('/cart/change') || xhr._url.includes('/cart/update') || xhr._url.includes('/cart/clear'))) {
                console.log('[wim-cart-gift-sync] Activando sincronización por XHR a ' + xhr._url);
                scheduleSync();
              }
            }
          });
          return originalXHRSend.apply(this, arguments);
        };

        // Ejecuta la sincronización en la carga inicial de la página
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => scheduleSync());
        } else {
          scheduleSync();
        }

        // MutationObserver para sincronizar después de que FoxTheme actualice el HTML en el DOM
        const observer = new MutationObserver((mutations) => {
          if (isSyncing) return;

          let shouldSync = false;
          for (const mutation of mutations) {
            if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
              const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
              for (const node of nodes) {
                if (node.nodeType === 1 && (
                  node.classList.contains('cart__item') ||
                  (node.querySelector && node.querySelector('.cart__item')) ||
                  (node.getAttribute && node.getAttribute('data-section-type') === 'cart-drawer')
                )) {
                  shouldSync = true;
                  break;
                }
              }
            }
            if (shouldSync) break;
          }
          if (shouldSync) {
            console.log('[wim-cart-gift-sync] DOM mutado, programando sincronización');
            scheduleSync();
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });

      }) ();
